/**
 * Lobby screen controller — handles both host and join modes.
 * Host: generates room code, sets battle config, waits for guest.
 * Join: enters room code, selects team, connects.
 * Both exchange HELLO and TEAM hash messages, then proceed to battle (Phase 4).
 */

import type { ScreenId } from "./home";
import type { Team, BattleConfig, TrainerProfile, PokemonSet } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, dropdown, numberInput, toast } from "../components";
import { getAllTeams, createEmptyTeam, getLastTeamId, setLastTeamId } from "../../data/teams";
import { loadTrainerProfile } from "./trainer";
import { generateRoomCode } from "../../p2p/protocol";
import { Connection } from "../../p2p/connection";
import { ManualConnection } from "../../p2p/manual";
import { WebSocketSignalingConnection } from "../../p2p/websocket-relay";
import type { IP2PConnection, ConnectionState } from "../../p2p/base";
import { SyncManager, type SyncState } from "../../p2p/sync";
import { BATTLE_BACKGROUNDS, randomBattleBackground } from "../../data/assets";
import { DEFAULT_LEVEL, DEFAULT_TURN_TIMER, RELAY_URL, SIGNALING_URL } from "../../config/constants";
import { createBattleSession } from "../battle-session";

/** In-memory ring buffer for connection diagnostics, visible in the UI. */
const MAX_LOG_LINES = 100;
let logBuffer: string[] = [];

/** Capture a log line into the buffer. Also writes to original console. */
export function captureLog(level: "log" | "warn" | "error", ...args: unknown[]): void {
  const line = `[${new Date().toLocaleTimeString()}] ${args.map((a) => String(a)).join(" ")}`;
  logBuffer.push(line);
  if (logBuffer.length > MAX_LOG_LINES) logBuffer.shift();
  // Use stored originals to avoid infinite loop after installLogCapture replaces console methods
  switch (level) {
    case "warn": _origConsoleWarn(...args); break;
    case "error": _origConsoleError(...args); break;
    default: _origConsoleLog(...args); break;
  }
}

/** Store original console methods to avoid infinite loops in captureLog. */
let _origConsoleLog: (...args: unknown[]) => void = console.log;
let _origConsoleWarn: (...args: unknown[]) => void = console.warn;
let _origConsoleError: (...args: unknown[]) => void = console.error;

/** Replace global console methods with buffered versions for diagnostics. */
function installLogCapture(): void {
  if ((window as any).__logCaptureInstalled) return;
  (window as any).__logCaptureInstalled = true;
  _origConsoleLog = console.log;
  _origConsoleWarn = console.warn;
  _origConsoleError = console.error;
  console.log = (...args: unknown[]) => { captureLog("log", ...args); };
  console.warn = (...args: unknown[]) => { captureLog("warn", ...args); };
  console.error = (...args: unknown[]) => { captureLog("error", ...args); };
}

export function renderLobby(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
  isHost: boolean,
): void {
  clearChildren(container);

  // Install console capture for diagnostics
  installLogCapture();
  captureLog("log", "[Lobby] rendering lobby, isHost:", isHost);

  const header = el("header", { className: "app-header" });
  header.appendChild(el("h1", { text: isHost ? "Host Battle" : "Join Battle" }));
  header.appendChild(button("Back to Home", () => {
    cleanupConn();
    navigate("home");
  }, { variant: "secondary" }));
  container.appendChild(header);

  const main = el("main", { className: "app-main lobby-main" });

  // State
  let conn: IP2PConnection | null = null;
  let sync: SyncManager | null = null;
  let roomCode = "";
  let selectedTeamId = "";
  let allTeams: Team[] = [];
  let currentConfig: BattleConfig = {
    level: DEFAULT_LEVEL,
    turnTimerSeconds: DEFAULT_TURN_TIMER,
    battleBackground: randomBattleBackground().id,
  };
  let connectionState: ConnectionState = "disconnected";
  let readyBtn: HTMLButtonElement | null = null;
  let isLocalReady = false;
  let handshakePanel: HTMLElement | null = null;
  let handshakeStatus: HTMLElement | null = null;
  let handshakeRetry: HTMLButtonElement | null = null;

  // Store references to action buttons so we can reset them on failure
  let hostActionBtn: HTMLButtonElement | null = null;
  let joinActionBtn: HTMLButtonElement | null = null;
  let isHostingMode = false;

  // Status display
  const statusEl = el("div", { className: "lobby-status" });
  const diagnosticsEl = el("div", { className: "lobby-diagnostics" });
  let diagnosticsOpen = false;
  function updateDiagnostics(): void {
    clearChildren(diagnosticsEl);
    const details = el("details", { className: "diagnostics-details" });
    const summary = el("summary", { text: "Connection Diagnostics" }) as HTMLElement;
    details.appendChild(summary);
    const ul = el("ul");
    const connType = conn
      ? "getLocalPeerId" in conn
        ? "WebSocket Signaling"
        : "getPeerIds" in conn
          ? "Trystero"
          : "Manual"
      : "None";
    const peerIds = conn?.getPeerIds() ?? [];
    const items = [
      `Connection type: ${connType}`,
      `State: ${connectionState}`,
      `Signaling URL: ${SIGNALING_URL || "(not configured)"}`,
      `Tracker URL: ${RELAY_URL || "(using public trackers)"}`,
      `Room code: ${roomCode || "not set"}`,
      `Peer IDs: ${peerIds.length > 0 ? peerIds.join(", ") : "none"}`,
    ];
    for (const item of items) {
      ul.appendChild(el("li", { text: item }));
    }
    // Add log buffer
    const logLi = el("li");
    const logDetails = el("details");
    const logSummary = el("summary", { text: `Log (${logBuffer.length} lines)` }) as HTMLElement;
    logDetails.appendChild(logSummary);
    const logPre = el("pre", {
      className: "diagnostics-log",
      text: logBuffer.join("\n"),
    }) as HTMLPreElement;
    logDetails.appendChild(logPre);
    logLi.appendChild(logDetails);
    ul.appendChild(logLi);
    details.appendChild(ul);
    diagnosticsEl.appendChild(details);
    (details as HTMLDetailsElement).open = diagnosticsOpen;
    details.addEventListener("toggle", () => {
      diagnosticsOpen = (details as HTMLDetailsElement).open;
    });
  }
  function updateStatus(): void {
    clearChildren(statusEl);
    const stateText: Record<ConnectionState, string> = {
      disconnected: "Not connected",
      connecting: "Connecting...",
      connected: "Connected to opponent!",
      reconnecting: "Reconnecting...",
      error: "Connection error",
    };
    statusEl.appendChild(el("p", { text: stateText[connectionState], className: `lobby-status-text lobby-status-${connectionState}` }));

    if (sync) {
      const s = sync.getState();
      if (s.peerProfile) {
        statusEl.appendChild(el("p", { text: `Opponent: ${s.peerProfile.name}`, className: "lobby-info" }));
      }
      if (s.peerTeamHash) {
        statusEl.appendChild(el("p", { text: `Opponent team verified ✓`, className: "lobby-info" }));
      }
      if (s.config) {
        statusEl.appendChild(el("p", { text: `Level: ${s.config.level} | Timer: ${s.config.turnTimerSeconds}s`, className: "lobby-info" }));
      }
    }
    updateDiagnostics();
  }

  // Team selector
  const teamSection = el("section", { className: "lobby-section" });
  teamSection.appendChild(el("h2", { text: "Select Your Team" }));
  const teamSelect = el("select", { className: "dropdown" }) as HTMLSelectElement;
  teamSection.appendChild(teamSelect);
  main.appendChild(teamSection);

  // Load teams
  void getAllTeams().then((teams) => {
    allTeams = teams;
    if (teams.length === 0) {
      const t = createEmptyTeam();
      allTeams = [t];
    }
    clearChildren(teamSelect);
    const lastId = getLastTeamId() ?? allTeams[0].id;
    selectedTeamId = lastId;
    for (const t of allTeams) {
      const opt = el("option", { text: t.name || "Untitled", attrs: { value: t.id } });
      teamSelect.appendChild(opt);
    }
    teamSelect.value = selectedTeamId;
    teamSelect.onchange = () => { selectedTeamId = teamSelect.value; setLastTeamId(selectedTeamId); };
  });

  if (isHost) {
    // Host: room code display + battle config
    const codeSection = el("section", { className: "lobby-section" });
    codeSection.appendChild(el("h2", { text: "Room Code" }));

    roomCode = generateRoomCode();
    const codeDisplay = el("div", { className: "room-code-display", text: roomCode });
    codeSection.appendChild(codeDisplay);

    const codeActions = el("div", { className: "actions-bar" });
    codeActions.appendChild(button("Copy Code", () => {
      navigator.clipboard.writeText(roomCode).then(() => toast("Code copied!", "success"));
    }, { variant: "secondary" }));
    codeActions.appendChild(button("Copy Link", () => {
      const link = `${window.location.origin}/#join=${roomCode}`;
      navigator.clipboard.writeText(link).then(() => toast("Link copied!", "success"));
    }, { variant: "secondary" }));
    codeActions.appendChild(button("New Code", () => {
      roomCode = generateRoomCode();
      codeDisplay.textContent = roomCode;
    }, { variant: "secondary" }));
    codeSection.appendChild(codeActions);
    main.appendChild(codeSection);

    // Battle config
    const configSection = el("section", { className: "lobby-section" });
    configSection.appendChild(el("h2", { text: "Battle Settings" }));
    configSection.appendChild(numberInput("Level", currentConfig.level, (v) => { currentConfig.level = v; }, { min: 1, max: 100 }).container);
    configSection.appendChild(numberInput("Turn Timer (seconds, 0 = off)", currentConfig.turnTimerSeconds, (v) => { currentConfig.turnTimerSeconds = v; }, { min: 0, max: 300 }).container);
    configSection.appendChild(dropdown(
      "Battle Background",
      [{ value: "", label: "Random" }, ...BATTLE_BACKGROUNDS.map((b) => ({ value: b.id, label: b.name }))],
      currentConfig.battleBackground,
      (v) => { currentConfig.battleBackground = v || randomBattleBackground().id; },
    ).container);
    main.appendChild(configSection);

    // Start hosting button — store ref directly; toggles to "Stop Hosting" while waiting
    hostActionBtn = button("Start Hosting", async () => {
      if (isHostingMode) {
        // "Stop Hosting" was clicked — cancel
        resetActionButton();
        return;
      }
      isHostingMode = true;
      hostActionBtn!.disabled = true;
      hostActionBtn!.textContent = "Waiting for opponent...";
      // After a brief delay, update to "Stop Hosting" so host can cancel
      setTimeout(() => {
        if (hostActionBtn && isHostingMode && connectionState === "connecting") {
          hostActionBtn.textContent = "Stop Hosting";
          hostActionBtn.disabled = false;
          hostActionBtn.classList.add("btn-danger");
        }
      }, 500);
      await startConnection(true);
    });
    main.appendChild(hostActionBtn);
    const hostHint = el("p", { text: "Share this room code with your opponent. Wait here until they join — the battle can only start once both players are connected.", className: "lobby-hint" });
    main.appendChild(hostHint);

    // Handshake progress panel (hidden until peer joins)
    handshakePanel = el("div", { className: "lobby-handshake-panel" });
    handshakeStatus = el("p", { className: "lobby-handshake-status", text: "" });
    handshakeRetry = button("Retry Handshake", () => {
      const profile = loadTrainerProfile();
      void runHandshake(true, profile);
    }, { variant: "secondary" });
    handshakeRetry.style.display = "none";
    handshakePanel.appendChild(handshakeStatus);
    handshakePanel.appendChild(handshakeRetry);
    main.appendChild(handshakePanel);

    // Manual fallback option for host
    const manualHostSection = el("section", { className: "lobby-section" });
    manualHostSection.appendChild(el("h2", { text: "Manual Connection Fallback" }));
    manualHostSection.appendChild(el("p", { text: "If Trystero DHT is blocked, use this to create a WebRTC offer and share it with your opponent out-of-band." }));
    const offerText = el("textarea", { className: "manual-sdp-text", attrs: { readonly: "readonly", placeholder: "Offer will appear here..." } }) as HTMLTextAreaElement;
    manualHostSection.appendChild(offerText);
    const offerBtn = button("Create Offer", async () => {
      offerBtn.disabled = true;
      offerBtn.textContent = "Creating...";
      await startManualConnection(true);
      const offer = await (conn as unknown as ManualConnection).createOffer();
      offerText.value = offer;
      navigator.clipboard.writeText(offer).then(() => toast("Offer copied!", "success"));
    });
    manualHostSection.appendChild(offerBtn);
    main.appendChild(manualHostSection);

  } else {
    // Manual fallback option for guest
    const manualGuestSection = el("section", { className: "lobby-section" });
    manualGuestSection.appendChild(el("h2", { text: "Manual Connection Fallback" }));
    manualGuestSection.appendChild(el("p", { text: "If Trystero DHT is blocked, paste the host's WebRTC offer here and share your answer back." }));
    const offerInput = el("textarea", { className: "manual-sdp-text", attrs: { placeholder: "Paste host's offer here..." } }) as HTMLTextAreaElement;
    manualGuestSection.appendChild(offerInput);
    const answerText = el("textarea", { className: "manual-sdp-text", attrs: { readonly: "readonly", placeholder: "Answer will appear here..." } }) as HTMLTextAreaElement;
    manualGuestSection.appendChild(answerText);
    const answerBtn = button("Create Answer", async () => {
      const offer = offerInput.value.trim();
      if (!offer) { toast("Paste the host's offer first", "error"); return; }
      answerBtn.disabled = true;
      answerBtn.textContent = "Creating...";
      await startManualConnection(false);
      const answer = await (conn as unknown as ManualConnection).acceptOffer(offer);
      answerText.value = answer;
      navigator.clipboard.writeText(answer).then(() => toast("Answer copied!", "success"));
    });
    manualGuestSection.appendChild(answerBtn);
    main.appendChild(manualGuestSection);

    // Guest: enter room code
    const joinSection = el("section", { className: "lobby-section" });
    joinSection.appendChild(el("h2", { text: "Enter Room Code" }));
    const guestHint = el("p", { text: "Make sure the host is already waiting in their lobby before you connect.", className: "lobby-hint" });
    joinSection.appendChild(guestHint);
    const codeInput = el("input", {
      className: "text-input room-code-input",
      attrs: { type: "text", placeholder: "ABC123", maxlength: "6" },
    }) as HTMLInputElement;
    codeInput.style.textTransform = "uppercase";
    joinSection.appendChild(codeInput);

    // Connect button — store ref directly
    joinActionBtn = button("Connect", async () => {
      const code = codeInput.value.trim().toUpperCase();
      if (code.length !== 6) {
        toast("Room code must be 6 characters", "error");
        return;
      }
      roomCode = code;
      joinActionBtn!.disabled = true;
      joinActionBtn!.textContent = "Connecting...";
      await startConnection(false);
    });
    joinSection.appendChild(joinActionBtn);

    // Check URL for join code
    const hashMatch = window.location.hash.match(/join=([A-Z0-9]{6})/i);
    if (hashMatch) {
      codeInput.value = hashMatch[1].toUpperCase();
    }

    main.appendChild(joinSection);
  }

  // Status and diagnostics
  main.appendChild(statusEl);
  main.appendChild(diagnosticsEl);
  updateStatus();

  container.appendChild(main);

  /** Start the P2P connection and sync handshake. */
  async function startConnection(hostMode: boolean): Promise<void> {
    const profile = loadTrainerProfile();
    const useSignaling = Boolean(SIGNALING_URL);

    conn = useSignaling
      ? new WebSocketSignalingConnection({
          onStateChange: (state) => {
            onConnectionStateChange(state);
            if (state === "error") {
              resetActionButton();
            } else if (state === "disconnected" && sync?.getState().phase !== "ended") {
              toast("Opponent disconnected", "warning");
            }
          },
          onPeerJoin: (_peerId) => {
            if (isHostingMode && hostActionBtn) {
              hostActionBtn.style.display = "none";
            }
            toast("Opponent connected!", "success");
            void runHandshake(hostMode, profile);
          },
          onPeerLeave: (_peerId) => {
            updateStatus();
          },
          onMessage: (msg) => {
            sync?.handleMessage(msg);
          },
          onPeerReconnect: () => {
            onConnectionPeerReconnect();
          },
        })
      : new Connection({
      onStateChange: (state) => {
        onConnectionStateChange(state);
        if (state === "error") {
          toast("Connection failed. Check the tracker URL and console logs.", "error");
          resetActionButton();
        } else if (state === "disconnected" && sync?.getState().phase !== "ended") {
          toast("Opponent disconnected", "warning");
        }
      },
      onPeerJoin: (_peerId) => {
        // Peer joined — hide "Stop Hosting" if visible
        if (isHostingMode && hostActionBtn) {
          hostActionBtn.style.display = "none";
        }
        toast("Opponent connected!", "success");
        // Send HELLO, TEAM, and (if host) config; retry briefly if the first attempt is lost
        void runHandshake(hostMode, profile);
      },
      onPeerLeave: (_peerId) => {
        updateStatus();
      },
      onMessage: (msg) => {
        sync?.handleMessage(msg);
      },
      onPeerReconnect: () => {
        onConnectionPeerReconnect();
      },
    });

    sync = new SyncManager(conn, hostMode, {
      onStateChange: (s: SyncState) => {
        updateStatus();
        updateReadyButton(s);
      },
      onPeerHello: (peerProfile: TrainerProfile, version: string) => {
        toast(`Opponent: ${peerProfile.name} (v${version})`, "info");
      },
      onPeerTeamHash: (_hash: string) => {
        updateStatus();
      },
      onConfigReceived: (config: BattleConfig) => {
        currentConfig = config;
        updateStatus();
      },
      onPeerReady: (peerProfile: TrainerProfile) => {
        toast(`${peerProfile.name} is ready!`, "info");
        updateReadyButton(sync!.getState());
      },
      onBothReady: (seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile, guestTeam: PokemonSet[], guestProfile: TrainerProfile) => {
        // Host only: both ready; emit BATTLE_START so both sides can start
        void sync!.sendBattleStart(seed, config, hostTeam, hostProfile);
        // Create host session (p1) with host team and guest team
        const hostTeamObj: Team = { id: "host", name: hostProfile.name, format: "gen5", members: hostTeam, createdAt: "", updatedAt: "" };
        const guestTeamObj: Team = { id: "guest", name: guestProfile.name, format: "gen5", members: guestTeam, createdAt: "", updatedAt: "" };
        createBattleSession(seed, config, sync!, hostProfile, guestProfile, hostTeamObj, guestTeamObj, true);
        sync!.setBattle();
        navigate("battle");
      },
      onActionReceived: () => {},
      onTurnReady: () => {},
      onPeerDisconnect: () => {
        toast("Opponent disconnected. Waiting for reconnect...", "warning");
        isLocalReady = false;
        sync?.resetReady();
        updateReadyButton(sync?.getState() ?? { phase: "ready" } as SyncState);
      },
      onPeerReconnect: () => {
        toast("Opponent reconnected!", "success");
      },
      onChat: () => {},
      onError: (msg: string) => toast(msg, "error"),
      onBattleStart: (seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile) => {
        // Guest receives BATTLE_START from host; create session and start battle
        const myTeam = allTeams.find((t) => t.id === selectedTeamId);
        if (!myTeam) { toast("No team selected", "error"); return; }
        const myProfile = loadTrainerProfile();
        createBattleSession(seed, config, sync!, myProfile, hostProfile, myTeam, { id: "host", name: hostProfile.name, format: "gen5", members: hostTeam, createdAt: "", updatedAt: "" }, false);
        sync!.setBattle();
        navigate("battle");
      },
      onGuestTeam: () => {},
    });

    try {
      if (conn instanceof WebSocketSignalingConnection) {
        await conn.join(roomCode, hostMode);
      } else {
        await (conn as Connection).join(roomCode, hostMode);
      }
    } catch (err) {
      captureLog("error", "[Lobby] join failed:", err);
      const message = err instanceof Error ? err.message : "Failed to connect. Please try again.";
      toast(message, "error");
      resetActionButton();
    }
  }

  /** Reset the action button after a failure so user can try again. */
  function resetActionButton(): void {
    isHostingMode = false;
    if (hostActionBtn) {
      hostActionBtn.disabled = false;
      hostActionBtn.textContent = "Start Hosting";
      hostActionBtn.style.display = "";
      hostActionBtn.classList.remove("btn-danger");
    }
    if (joinActionBtn) {
      joinActionBtn.disabled = false;
      joinActionBtn.textContent = "Connect";
    }
    // Clear stale connection/sync so retry doesn't collide
    if (sync) {
      sync.stopHeartbeat();
      sync = null;
    }
    if (conn) {
      void conn.leave().catch(() => {});
      conn = null;
    }
    connectionState = "disconnected";
    updateStatus();
  }

  async function runHandshake(hostMode: boolean, profile: TrainerProfile): Promise<void> {
    const team = allTeams.find((t) => t.id === selectedTeamId);
    let attempts = 0;
    const maxAttempts = 10;
    if (handshakeStatus) handshakeStatus.textContent = "Waiting for handshake...";
    if (handshakeRetry) handshakeRetry.style.display = "none";
    const interval = setInterval(() => {
      attempts++;
      if (!sync || sync.getState().phase === "battle" || sync.getState().phase === "ended" || attempts > maxAttempts) {
        clearInterval(interval);
        if (handshakeStatus) {
          const s = sync?.getState();
          if (s && !s.peerProfile) handshakeStatus.textContent = "Handshake timed out waiting for opponent profile.";
          else if (s && !s.myProfile) handshakeStatus.textContent = "Handshake timed out sending our profile.";
          else if (s && !s.myTeamHash) handshakeStatus.textContent = "Handshake timed out sending our team.";
          else if (hostMode && s && !s.config) handshakeStatus.textContent = "Handshake timed out sending battle config.";
          else handshakeStatus.textContent = "Handshake timed out.";
        }
        if (handshakeRetry) handshakeRetry.style.display = "inline-block";
        return;
      }
      // Only resend while we have not completed the handshake
      const s = sync.getState();
      if (handshakeStatus) {
        const missing: string[] = [];
        if (!s.peerProfile) missing.push("opponent profile");
        if (!s.myProfile) missing.push("our profile");
        if (!s.myTeamHash) missing.push("our team");
        if (hostMode && !s.config) missing.push("battle config");
        if (missing.length === 0) {
          handshakeStatus.textContent = "Handshake complete!";
        } else {
          handshakeStatus.textContent = `Waiting for handshake: ${missing.join(", ")}...`;
        }
      }
      if (!s.myProfile) {
        void sync.sendHello(profile);
      }
      if (team && !s.myTeamHash) {
        void sync.sendTeamHash(team.members);
      }
      if (hostMode && !s.config) {
        void sync.sendConfig(currentConfig);
      }
      if (s.peerProfile && s.myProfile && s.myTeamHash && (hostMode ? s.config : true)) {
        clearInterval(interval);
        if (handshakeStatus) handshakeStatus.textContent = "Handshake complete!";
        if (handshakeRetry) handshakeRetry.style.display = "none";
      }
    }, 500);
  }

  /** Start a manual WebRTC connection using the ManualConnection class. */
  async function startManualConnection(hostMode: boolean): Promise<void> {
    const profile = loadTrainerProfile();
    const manualConn = new ManualConnection(
      {
        onStateChange: (state) => {
          onConnectionStateChange(state);
        },
        onPeerJoin: () => {
          toast("Peer connected manually!", "success");
          void sync?.sendHello(profile);
          const team = allTeams.find((t) => t.id === selectedTeamId);
          if (team) {
            void sync?.sendTeamHash(team.members);
          }
          if (hostMode && sync) {
            void sync.sendConfig(currentConfig);
          }
        },
        onPeerLeave: () => {
          updateStatus();
          toast("Peer disconnected", "warning");
        },
        onMessage: (msg) => {
          sync?.handleMessage(msg);
        },
      },
      {
        onOfferCreated: (offer) => {
          navigator.clipboard.writeText(offer).then(() => toast("Offer copied!", "success")).catch(() => { /* ignore clipboard errors */ });
        },
        onAnswerCreated: (answer) => {
          navigator.clipboard.writeText(answer).then(() => toast("Answer copied!", "success")).catch(() => { /* ignore clipboard errors */ });
        },
      },
    );

    conn = manualConn;
    sync = newSyncManager(hostMode);
    sync.setPeerDisconnectCallback(() => {
      toast("Peer disconnected", "warning");
    });
    sync.setPeerReconnectCallback(() => {
      toast("Peer reconnected!", "success");
    });

    // For host: set up UI to accept the bundled answer from the guest
    if (hostMode) {
      const answerSection = el("section", { className: "lobby-section" });
      answerSection.appendChild(el("h2", { text: "Paste Guest's Answer" }));
      const answerInput = el("textarea", { className: "manual-sdp-text", attrs: { placeholder: "Paste guest's answer here..." } }) as HTMLTextAreaElement;
      answerSection.appendChild(answerInput);
      const acceptBtn = button("Accept Answer", async () => {
        const answer = answerInput.value.trim();
        if (!answer) { toast("Paste the guest's answer first", "error"); return; }
        acceptBtn.disabled = true;
        acceptBtn.textContent = "Connecting...";
        await manualConn.acceptAnswer(answer);
      });
      answerSection.appendChild(acceptBtn);
      main.appendChild(answerSection);
    }
  }

  function newSyncManager(hostMode: boolean): SyncManager {
    return new SyncManager(conn!, hostMode, {
      onStateChange: (s: SyncState) => {
        updateStatus();
        updateReadyButton(s);
      },
      onPeerHello: (peerProfile: TrainerProfile, version: string) => {
        toast(`Opponent: ${peerProfile.name} (v${version})`, "info");
      },
      onPeerTeamHash: (_hash: string) => {
        updateStatus();
      },
      onConfigReceived: (config: BattleConfig) => {
        currentConfig = config;
        updateStatus();
      },
      onPeerReady: (peerProfile: TrainerProfile) => {
        toast(`${peerProfile.name} is ready!`, "info");
        updateReadyButton(sync!.getState());
      },
      onBothReady: (seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile, guestTeam: PokemonSet[], guestProfile: TrainerProfile) => {
        void sync!.sendBattleStart(seed, config, hostTeam, hostProfile);
        const hostTeamObj: Team = { id: "host", name: hostProfile.name, format: "gen5", members: hostTeam, createdAt: "", updatedAt: "" };
        const guestTeamObj: Team = { id: "guest", name: guestProfile.name, format: "gen5", members: guestTeam, createdAt: "", updatedAt: "" };
        createBattleSession(seed, config, sync!, hostProfile, guestProfile, hostTeamObj, guestTeamObj, true);
        sync!.setBattle();
        navigate("battle");
      },
      onActionReceived: () => {},
      onTurnReady: () => {},
      onChat: () => {},
      onPeerDisconnect: () => {
        toast("Opponent disconnected. Waiting for reconnect...", "warning");
        isLocalReady = false;
        sync?.resetReady();
        updateReadyButton(sync?.getState() ?? { phase: "ready" } as SyncState);
      },
      onPeerReconnect: () => {
        toast("Opponent reconnected!", "success");
      },
      onError: (msg: string) => toast(msg, "error"),
      onBattleStart: (seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile) => {
        const myTeam = allTeams.find((t) => t.id === selectedTeamId);
        if (!myTeam) { toast("No team selected", "error"); return; }
        const myProfile = loadTrainerProfile();
        createBattleSession(seed, config, sync!, myProfile, hostProfile, myTeam, { id: "host", name: hostProfile.name, format: "gen5", members: hostTeam, createdAt: "", updatedAt: "" }, false);
        sync!.setBattle();
        navigate("battle");
      },
      onGuestTeam: () => {},
    });
  }

  /** Render or update the ready button based on handshake/battle state. */
  function updateReadyButton(s: SyncState): void {
    if (!s || s.phase === "battle" || s.phase === "ended") {
      readyBtn?.remove();
      readyBtn = null;
      return;
    }
    const handshakeComplete = s.peerProfile && s.peerTeamHash && s.config;
    if (!handshakeComplete || s.phase === "waiting") {
      readyBtn?.remove();
      readyBtn = null;
      return;
    }
    if (!readyBtn) {
      readyBtn = button("Ready", async () => {
        const myTeam = allTeams.find((t) => t.id === selectedTeamId);
        if (!myTeam) { toast("Select a team first", "error"); return; }
        const myProfile = loadTrainerProfile();
        isLocalReady = true;
        readyBtn!.disabled = true;
        readyBtn!.textContent = "Waiting for opponent...";
        await sync!.sendReady(myTeam.members, myProfile);
      });
      readyBtn.classList.add("ready-btn");
      // Place ready button outside statusEl so updateStatus() doesn't clear it
      const readyContainer = el("div", { className: "ready-container" });
      readyContainer.appendChild(readyBtn);
      statusEl.parentElement?.insertBefore(readyContainer, statusEl.nextSibling);
    }
    if (isLocalReady) {
      readyBtn.disabled = true;
      readyBtn.textContent = s.peerReady ? "Starting battle..." : "Waiting for opponent...";
    } else if (s.peerReady) {
      readyBtn.textContent = "Opponent is ready — Ready?";
      readyBtn.disabled = false;
    } else {
      readyBtn.textContent = "Ready";
      readyBtn.disabled = false;
    }
  }

  // Start heartbeat once connected so disconnects are detected quickly
  function onConnectionStateChange(state: ConnectionState): void {
    connectionState = state;
    updateStatus();
    if (state === "connected" && sync) {
      sync.startHeartbeat();
    } else if (state === "reconnecting" && sync) {
      sync.markPeerDisconnected();
    } else if (state === "disconnected" && sync) {
      sync.stopHeartbeat();
      // Reset ready state if the battle hasn't started so a new peer can join
      if (sync.getState().phase !== "battle" && sync.getState().phase !== "ended") {
        isLocalReady = false;
        sync.resetReady();
        updateReadyButton(sync.getState());
      }
    }
  }

  // Wire connection reconnect event to sync
  function onConnectionPeerReconnect(): void {
    sync?.markPeerReconnected();
  }

  // Cleanup on navigation away. Declared as a function so the back button
  // (created earlier in this closure) can reference it safely.
  function cleanupConn(): void {
    sync?.stopHeartbeat();
    void conn?.leave();
  }

  // Expose cleanup so app.ts can call it when navigating away from lobby
  (container as HTMLElement & { __cleanupLobby?: () => void }).__cleanupLobby = cleanupConn;
}